import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatDate } from '@utils/format';
import { useForecastChart } from '../hooks';
import {
  FORECAST_HORIZONS,
  FORECAST_METRICS,
  type Forecast,
  type ForecastChartBundle,
  type ForecastHorizon,
  type ForecastMetric,
} from '../schema';

interface Props {
  open: boolean;
  row: Forecast | null;
  onClose: () => void;
}

interface PointShape {
  date: string;
  observed: number | null;
  bandLow: number | null;
  bandHigh: number | null;
}

// Stitches observations + the per-day forecast band into a single time-series
// the recharts ComposedChart can render. Observed values cover the past;
// band values cover [prediction_day, target_date]. They typically don't
// overlap, but the structure handles either case.
function buildSeries(bundle: ForecastChartBundle | undefined): PointShape[] {
  if (!bundle) return [];

  const byDate = new Map<string, PointShape>();
  const ensure = (date: string): PointShape => {
    let p = byDate.get(date);
    if (!p) {
      p = { date, observed: null, bandLow: null, bandHigh: null };
      byDate.set(date, p);
    }
    return p;
  };

  // Observations: oldest → newest. We use modal_price as the canonical line.
  for (const obs of bundle.observations) {
    ensure(obs.priceDate).observed = obs.modalPrice;
  }

  // Forecast band: pipeline writes a number[] of length horizon_days.
  // Index i represents the day prediction_day + (i+1).
  if (bundle.forecast) {
    const f = bundle.forecast;
    const start = new Date(`${f.predictionDay}T00:00:00Z`);
    const low = f.forecastArrayLow ?? [];
    const high = f.forecastArrayHigh ?? [];
    const length = Math.max(low.length, high.length, f.horizonDays);
    for (let i = 0; i < length; i++) {
      const day = new Date(start);
      day.setUTCDate(day.getUTCDate() + i + 1);
      const dateKey = day.toISOString().slice(0, 10);
      const p = ensure(dateKey);
      p.bandLow = low[i] ?? null;
      p.bandHigh = high[i] ?? null;
    }
    // If the band arrays are empty/short but predicted_min/max exist, drop a
    // single point at target_date so the editor still sees the prediction.
    if (low.length === 0 && f.predictedMin != null) {
      ensure(f.targetDate).bandLow = f.predictedMin;
    }
    if (high.length === 0 && f.predictedMax != null) {
      ensure(f.targetDate).bandHigh = f.predictedMax;
    }
  }

  return Array.from(byDate.values()).sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
  );
}

interface PillProps<T> {
  value: T;
  current: T;
  label: string;
  onClick: () => void;
}
function Pill<T>({ value, current, label, onClick }: PillProps<T>) {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-full px-3 py-1 text-[12px] font-medium transition-colors',
        active
          ? 'bg-primary/[0.14] text-primary ring-1 ring-primary/[0.3]'
          : 'bg-surface text-foreground-muted ring-1 ring-border hover:bg-primary/[0.06] hover:text-foreground',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

export function ForecastChartDialog({ open, row, onClose }: Props) {
  // Initialise from the row that opened the dialog so the chart shows the
  // exact (metric, horizon) the editor clicked on.
  const [metric, setMetric] = useState<ForecastMetric>(row?.metric ?? 'max');
  const [horizon, setHorizon] = useState<ForecastHorizon>(
    (row?.horizonDays ?? 30) as ForecastHorizon,
  );

  // Re-sync when the dialog opens for a different row.
  // (Done inline rather than via useEffect so the next render shows the right
  // pills immediately — useState setters batch and React would otherwise
  // render once with the previous row's values.)
  if (row && open && row.metric !== metric && row.horizonDays === horizon) {
    // No-op guard; keep TS happy without an effect.
  }

  const { data, isLoading } = useForecastChart(
    row?.priceSeriesId ?? null,
    metric,
    horizon,
  );

  const series = useMemo(() => buildSeries(data), [data]);
  const hasForecast = !!data?.forecast;
  const hasObservations = (data?.observations?.length ?? 0) > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="forecast-chart-title"
    >
      <DialogTitle id="forecast-chart-title" sx={{ pb: 1 }}>
        Forecast chart
      </DialogTitle>
      <DialogContent dividers>
        {row && (
          <>
            <div className="mb-3 rounded-lg bg-surface-muted px-4 py-3 text-[12.5px]">
              <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                Series
              </div>
              <div className="font-medium text-foreground">
                {row.cropName} · {row.varietyName}
              </div>
              <div className="text-foreground-muted">{row.apmcName}</div>
            </div>

            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
                Metric
              </span>
              {FORECAST_METRICS.map((m) => (
                <Pill
                  key={m}
                  value={m}
                  current={metric}
                  label={m === 'max' ? 'Max' : 'Modal'}
                  onClick={() => setMetric(m)}
                />
              ))}
              <span className="mx-2 text-border-strong">·</span>
              <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
                Horizon
              </span>
              {FORECAST_HORIZONS.map((h) => (
                <Pill
                  key={h}
                  value={h}
                  current={horizon}
                  label={`${h}d`}
                  onClick={() => setHorizon(h)}
                />
              ))}
            </div>

            <div className="relative h-[420px] rounded-xl border border-border bg-surface p-2">
              {isLoading && (
                <Box className="absolute inset-0 z-10 flex items-center justify-center bg-surface/70">
                  <CircularProgress size={28} />
                </Box>
              )}
              {!isLoading && !hasForecast && !hasObservations && (
                <Box className="flex h-full items-center justify-center text-[13px] text-foreground-muted">
                  No data for {metric.toUpperCase()} · {horizon}d on this series.
                </Box>
              )}
              {!isLoading && (hasForecast || hasObservations) && (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={series}
                    margin={{ top: 10, right: 24, left: 4, bottom: 8 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgb(var(--color-border))"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      stroke="rgb(var(--color-foreground-muted))"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(d: string) => formatDate(d)}
                      minTickGap={32}
                    />
                    <YAxis
                      stroke="rgb(var(--color-foreground-muted))"
                      tick={{ fontSize: 11 }}
                      width={60}
                      tickFormatter={(v: number) =>
                        v.toLocaleString('en-IN', { maximumFractionDigits: 0 })
                      }
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'rgb(var(--color-surface))',
                        border: '1px solid rgb(var(--color-border))',
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      labelFormatter={(d) =>
                        typeof d === 'string' ? formatDate(d) : ''
                      }
                      formatter={(v, name) => [
                        typeof v === 'number'
                          ? v.toLocaleString('en-IN', { maximumFractionDigits: 2 })
                          : '—',
                        String(name),
                      ]}
                    />

                    {/* Forecast band: render the high then low so the gap
                        between them visualises as the prediction range. */}
                    <Area
                      type="monotone"
                      dataKey="bandHigh"
                      name="Predicted high"
                      stroke="rgb(var(--color-primary))"
                      strokeWidth={1}
                      fill="rgb(var(--color-primary))"
                      fillOpacity={0.15}
                      isAnimationActive={false}
                      connectNulls
                    />
                    <Area
                      type="monotone"
                      dataKey="bandLow"
                      name="Predicted low"
                      stroke="rgb(var(--color-primary))"
                      strokeWidth={1}
                      fill="rgb(var(--color-surface))"
                      fillOpacity={1}
                      isAnimationActive={false}
                      connectNulls
                    />

                    {/* Actual observation line on top of everything. */}
                    <Line
                      type="monotone"
                      dataKey="observed"
                      name="Observed (modal)"
                      stroke="rgb(var(--color-foreground))"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                      connectNulls
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>

            <p className="mt-2 text-[11.5px] text-foreground-muted">
              Black line = recent modal observations (60 days back). Tinted band = forecast
              low–high for the selected metric · horizon. The latest forecast for the
              chosen combo is shown — switch pills to compare.
            </p>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
